import Card from '@/components/Card/Card';
import UploadField from '@/components/UploadField/UploadField';
import { FILE_TYPE_MAP } from '@/components/UploadField/utils';
import PostcodeInputCity from '@/components/postcodeInput/PostcodeInputCity';
import FormikStreetField from '@/components/streetInput/FormikStreetField';
import { TextField } from '@/components/textField';
import { usePostcodeSelect } from '@/hooks/usePostcodeSelect';
import { useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './B2BPersonalDataForm.module.scss';
import type { B2BFormData } from './PageB2B';

export interface PersonalDataFormProps {
  onFilesChanged: (files: File[]) => void;
}

const B2BPersonalDataForm: React.FC<PersonalDataFormProps> = ({ onFilesChanged }) => {
  const formik = useFormikContext<B2BFormData>();
  const postcodeState = usePostcodeSelect();
  const { t } = useTranslation();

  useEffect(() => {
    formik.setFieldValue('city', postcodeState.selectedValue.cityName);
  }, [postcodeState.selectedValue.cityName]);

  return (
    <div className={classes.B2BPersonalDataForm}>
      <Card className={classes.Card} variant="Box">
        <div className={classes.DataForm}>
          <div className={classes.Headline}>{t('Persönliche Daten')}</div>
          <div className={classes.InputRow}>
            <TextField
              required
              id="firstName"
              name="firstName"
              label="Vorname"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.firstName && Boolean(formik.errors.firstName)}
              helperText={formik.touched.firstName && formik.errors.firstName}
              fullWidth
              inputProps={{ 'data-testid': 'B2B-personal-data-form-firstName-input' }}
            />

            <TextField
              required
              id="lastName"
              name="lastName"
              label="Nachname"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
              fullWidth
              inputProps={{ 'data-testid': 'B2B-personal-data-form-firstName-input' }}
            />
            <TextField
              required
              id="company"
              name="company"
              label="Firma"
              value={formik.values.company}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.company && Boolean(formik.errors.company)}
              helperText={formik.touched.company && formik.errors.company}
              fullWidth
              inputProps={{ 'data-testid': 'B2B-personal-data-form-company-input' }}
            />
          </div>
          <div className={classes.Headline}>Kontaktdaten</div>
          <div className={classes.InputRow}>
            <TextField
              required
              id="email"
              name="email"
              label="E-Mail"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              fullWidth
              inputProps={{ 'data-testid': 'B2B-personal-data-form-email-input' }}
            />
            <TextField
              id="phone"
              name="phone"
              label="Telefon"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              inputProps={{ 'data-testid': 'B2B-personal-data-form-phone-input' }}
            />
          </div>
          <div className={classes.Headline}>{t('Adresse')}</div>
          <div className={classes.InputRow}>
            <TextField
              required
              id="postcode"
              name="postcode"
              label="Postleitzahl"
              value={formik.values.postcode}
              onChange={(e: React.FocusEvent<any, Element>) => {
                postcodeState.handleInputChange(e);
                formik.setFieldValue('postcode', postcodeState.selectedValue.postcode);
                formik.setFieldValue('city', postcodeState.selectedValue.cityName);
                formik.handleChange(e);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.postcode && Boolean(formik.errors.postcode)}
              helperText={formik.touched.postcode && formik.errors.postcode}
              inputProps={{ maxLength: 5, 'data-testid': 'B2B-personal-data-form-postcode-input' }}
              fullWidth
            />
            <TextField
              required
              id="city"
              name="city"
              label="Stadt"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={formik.touched.city && formik.errors.city}
              fullWidth
              inputProps={{ 'data-testid': 'B2B-personal-data-form-city-input' }}
              disabled
            />
            {/* City Selection Dialog */}
            <PostcodeInputCity state={postcodeState} onlyDialog={true} />
          </div>
          <div className={classes.InputRow}>
            <FormikStreetField
              required
              id="street"
              name="street"
              label="Straße"
              postcode={formik.values.postcode}
              city={formik.values.city}
            />
            <TextField
              required
              id="houseNumber"
              name="houseNumber"
              label="Hausnummer"
              value={formik.values.houseNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.houseNumber && Boolean(formik.errors.houseNumber)}
              helperText={formik.touched.houseNumber && formik.errors.houseNumber}
              fullWidth
              inputProps={{ 'data-testid': 'B2B-personal-data-form-houseNumber-input' }}
            />
          </div>
          <div className={classes.Headline}>{t('Lastprofil RLM-Zähler')}</div>
          <div className={classes.InputRow}>
            <UploadField
              name="uploadedFiles"
              label="Datei(en) auswählen"
              acceptedFileTypes={[FILE_TYPE_MAP.CSV, FILE_TYPE_MAP.XLS, FILE_TYPE_MAP.XLSX]}
              maxFiles={4}
              maxFileSize={'20MB'}
              required={false}
              onChange={(event) => {
                const files = Array.from(event.target.files || []);
                onFilesChanged(files);
                formik.setFieldValue('uploadedFiles', files);
              }}
              onBlur={() => formik.handleBlur({ target: { name: 'uploadedFiles' } } as any)}
              fileUploadText="Datei"
            />
          </div>

          <div className={classes.Headline}>{t('Bemerkungen')}</div>
          <TextField
            id="comments"
            name="comments"
            multiline
            rows={4}
            value={formik.values.comments}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            inputProps={{ 'data-testid': 'B2B-personal-data-form-comments-input' }}
          />
        </div>
      </Card>
    </div>
  );
};

export default B2BPersonalDataForm;
